import {
  InputColumn,
  IssueFlaggerOutput,
  IssueFlaggerTable,
  IssueFlaggerTreeNode,
  IssueTriageTable,
  MorningstarData,
  SurveyData,
  StrategyRecommendationOutput,
  StrategyRecommendationTable,
  StrategyRecommendationTreeNode,
  RatioSourceRef,
} from './parameters-types';
import { Birthdate, calculateAgeFromBirthdate, calculateTimeHorizonYears, deriveRetirementYear } from '../../components/kyc-editor/kyc-schema';

/**
 * Helper function to get a value from an object using a dot-notation path
 */
function getValueByPath(obj: any, path: string): any {
  return path.split('.').reduce((o, key) => (o && o[key] !== undefined) ? o[key] : undefined, obj);
}

function normalizeNumericValue(value: any): number | undefined {
  if (typeof value === 'number' && !isNaN(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
}

function getRatioSourceValue(
  ref: RatioSourceRef,
  morningstarData: MorningstarData,
  surveyData: SurveyData
): number | undefined {
  if (ref.source === 'morningstarData') {
    return normalizeNumericValue(getValueByPath(morningstarData, ref.path as string));
  }
  return normalizeNumericValue(surveyData[ref.path]);
}

/**
 * Calculate age in decimal years from a date of birth string
 * @param dobString - ISO 8601 date string (YYYY-MM-DD format)
 * @returns Age in decimal years, or undefined if invalid date
 */
export function calculateAgeFromDOB(dobString: string): number | undefined {
  if (!dobString || typeof dobString !== 'string') {
    return undefined;
  }

  // Parse the date string (expecting ISO format YYYY-MM-DD)
  const dob = new Date(dobString);
  
  // Check if the date is valid
  if (isNaN(dob.getTime())) {
    return undefined;
  }

  const now = new Date();
  
  // Calculate age in decimal years
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < dob.getDate())) {
    age--;
  }
  
  // Add fractional year based on day of year
  const daysInYear = ((now.getFullYear() % 4 === 0 && now.getFullYear() % 100 !== 0) || now.getFullYear() % 400 === 0) ? 366 : 365;
  const dayOfYear = Math.floor((now.getTime() - new Date(now.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24));
  const dobDayOfYear = Math.floor((dob.getTime() - new Date(dob.getFullYear(), 0, 0).getTime()) / (1000 * 60 * 60 * 24));
  
  const fractionalYear = (dayOfYear - dobDayOfYear) / daysInYear;
  age += fractionalYear;
  
  return Math.round(age * 100) / 100; // Round to 2 decimal places
}

/**
 * Transform a column value based on column configuration
 * @param column - The input column configuration
 * @param rawValue - The raw value from the data source
 * @returns The transformed value for use in decision trees
 */
export function transformColumnValue(column: InputColumn, rawValue: any): any {
  // For date-based survey columns that should be transformed to age
  if (column.type === 'numerical' && 
      column.source === 'surveyAnswers' && 
      column.path === 'dob' && 
      typeof rawValue === 'string' && 
      rawValue.trim() !== '') {
    return calculateAgeFromDOB(rawValue);
  }
  
  // For other columns, return the raw value
  return rawValue;
}

/**
 * Helper function to extract a value from a column based on the input data
 */
function getColumnValue(
  column: InputColumn, 
  morningstarData: MorningstarData, 
  surveyData: SurveyData,
  issueOutputs: Record<string, IssueFlaggerOutput>
): any {
  let rawValue: any;
  
  switch (column.type) {
    case 'numerical':
      if (column.source === 'morningstarData') {
        rawValue = getValueByPath(morningstarData, column.path as string);
      } else if (column.source === 'surveyAnswers') {
        rawValue = surveyData[column.path];
      }
      break;
    case 'categorical':
      if (column.source === 'surveyAnswers') {
        rawValue = surveyData[column.path];
      }
      break;
    case 'ratio': {
      if (!column.numerator.length || !column.denominator.length) {
        return undefined;
      }

      const numeratorValues = column.numerator.map(ref =>
        getRatioSourceValue(ref, morningstarData, surveyData)
      );
      const denominatorValues = column.denominator.map(ref =>
        getRatioSourceValue(ref, morningstarData, surveyData)
      );

      if (
        numeratorValues.some(value => value === undefined) ||
        denominatorValues.some(value => value === undefined)
      ) {
        return undefined;
      }

      const numeratorSum = (numeratorValues as number[]).reduce((sum, value) => sum + value, 0);
      const denominatorSum = (denominatorValues as number[]).reduce((sum, value) => sum + value, 0);

      if (!denominatorSum) {
        return undefined;
      }

      return numeratorSum / denominatorSum;
    }
    case 'derived': {
      if (column.key === 'age') {
        const birthdate = (surveyData as any).birthdate as Birthdate | undefined;
        if (birthdate) {
          return calculateAgeFromBirthdate(birthdate);
        }
        const dobString = (surveyData as any).dob as string | undefined;
        return dobString ? calculateAgeFromDOB(dobString) : undefined;
      }
      if (column.key === 'timeHorizon') {
        const retirementYear = (surveyData as any).retirementYear as number | undefined;
        if (retirementYear) {
          return calculateTimeHorizonYears(retirementYear);
        }
        const legacyTimeHorizon = (surveyData as any).timeHorizon as string | undefined;
        const derivedYear = deriveRetirementYear(legacyTimeHorizon);
        return derivedYear ? calculateTimeHorizonYears(derivedYear) : undefined;
      }
      return undefined;
    }
    case 'lightStatus':
      return issueOutputs[column.parameterName]?.lightStatus;
    case 'tags':
      // Check if the tag exists in ANY of the issue outputs
      const tagID = column.tagID;
      return Object.values(issueOutputs).some(output => 
        output.tags?.includes(tagID)
      );
    default:
      return undefined;
  }
  
  // Apply transformation if needed
  return transformColumnValue(column, rawValue);
}

/**
 * Infer the output for a single issue flagger table
 */
export function inferIssue(
  table: IssueFlaggerTable,
  morningstarData: MorningstarData,
  surveyData: SurveyData,
  issueOutputs: Record<string, IssueFlaggerOutput> = {}
): IssueFlaggerOutput {
  return processIssueFlaggerTree(
    table.decisionTree,
    table.inputColumns,
    morningstarData,
    surveyData,
    issueOutputs
  );
}

/**
 * Process an issue flagger decision tree to determine the output
 */
function processIssueFlaggerTree(
  node: IssueFlaggerTreeNode,
  inputColumns: InputColumn[],
  morningstarData: MorningstarData,
  surveyData: SurveyData,
  issueOutputs: Record<string, IssueFlaggerOutput>
): IssueFlaggerOutput {
  // If we've reached a leaf node, return its output
  if (node.type === 'issueFlaggerLeaf') {
    return node.output;
  }

  // For split nodes, determine which child to follow
  if (node.type === 'numericalSplit') {
    const { columnIndex, ranges, defaultChild } = node.split;
    const column = inputColumns[columnIndex];
    const value = getColumnValue(column, morningstarData, surveyData, issueOutputs);
    
    // If value is undefined or not a number, use the default child
    if (value === undefined || typeof value !== 'number') {
      return processIssueFlaggerTree(defaultChild, inputColumns, morningstarData, surveyData, issueOutputs);
    }

    // Find the appropriate range
    for (const range of ranges) {
      if (value <= range.maxValue) {
        return processIssueFlaggerTree(range.childNode, inputColumns, morningstarData, surveyData, issueOutputs);
      }
    }

    // If no range matches, use the default child
    return processIssueFlaggerTree(defaultChild, inputColumns, morningstarData, surveyData, issueOutputs);
  }

  if (node.type === 'categoricalSplit') {
    const { columnIndex, categoryMap, defaultChild } = node.split;
    const column = inputColumns[columnIndex];
    const value = getColumnValue(column, morningstarData, surveyData, issueOutputs);
    
    // If value is undefined, use the default child
    if (value === undefined) {
      return processIssueFlaggerTree(defaultChild, inputColumns, morningstarData, surveyData, issueOutputs);
    }

    // Find the matching category
    for (const category of categoryMap) {
      if (value === category.category) {
        return processIssueFlaggerTree(category.childNode, inputColumns, morningstarData, surveyData, issueOutputs);
      }
    }

    // If no category matches, use the default child
    return processIssueFlaggerTree(defaultChild, inputColumns, morningstarData, surveyData, issueOutputs);
  }

  if (node.type === 'booleanSplit') {
    const { columnIndex, trueChild, falseChild } = node.split;
    const column = inputColumns[columnIndex];
    const value = getColumnValue(column, morningstarData, surveyData, issueOutputs);
    
    // If value is true and we have a true child, use it
    if (value === true && trueChild) {
      return processIssueFlaggerTree(trueChild, inputColumns, morningstarData, surveyData, issueOutputs);
    }
    // In all other cases (false, undefined, or no trueChild), use the false child
    return processIssueFlaggerTree(falseChild, inputColumns, morningstarData, surveyData, issueOutputs);
  }

  // This should never happen if the types are correct
  throw new Error(`Unknown node type: ${(node as any).type}`);
}

/**
 * Infer outputs for all issue flagger tables
 */
export function inferAllIssues(
  tables: IssueFlaggerTable[],
  morningstarData: MorningstarData,
  surveyData: SurveyData
): Record<string, IssueFlaggerOutput> {
  const outputs: Record<string, IssueFlaggerOutput> = {};
  
  // Process each table and store its output
  for (const table of tables) {
    // Each table can potentially use outputs from previously processed tables
    const output = inferIssue(table, morningstarData, surveyData, outputs);
    
    // Store the output with defaults for missing values
    outputs[table.id] = {
      lightStatus: output.lightStatus || 'green',
      explanation: output.explanation || '',
      tags: output.tags || []
    };
  }
  
  return outputs;
}

/**
 * Get the triage order of issues based on severity and the triage table
 */
export function getTriageOrder(
  issueOutputs: Record<string, IssueFlaggerOutput>,
  triageTable: IssueTriageTable,
  tables: IssueFlaggerTable[]
): string[] {
  // Group issues by light status
  const redIssues: string[] = [];
  const yellowIssues: string[] = [];
  const greenIssues: string[] = [];
  
  // Use the triage table's order for sorting within each severity level
  for (const id of triageTable.issueFlaggerIds) {
    const output = issueOutputs[id];
    if (!output) continue;
    
    switch (output.lightStatus) {
      case 'red':
        redIssues.push(id);
        break;
      case 'yellow':
        yellowIssues.push(id);
        break;
      case 'green':
        greenIssues.push(id);
        break;
    }
  }
  
  // Combine all issues in order of severity
  return [...redIssues, ...yellowIssues, ...greenIssues];
}

/**
 * Infer strategy recommendation based on issue outputs
 */
export function inferStrategy(
  table: StrategyRecommendationTable,
  morningstarData: MorningstarData,
  surveyData: SurveyData,
  issueOutputs: Record<string, IssueFlaggerOutput>
): StrategyRecommendationOutput {
  return processStrategyRecommendationTree(
    table.decisionTree,
    table.inputColumns,
    morningstarData,
    surveyData,
    issueOutputs
  );
}

/**
 * Process a strategy recommendation decision tree to determine the output
 */
function processStrategyRecommendationTree(
  node: StrategyRecommendationTreeNode,
  inputColumns: InputColumn[],
  morningstarData: MorningstarData,
  surveyData: SurveyData,
  issueOutputs: Record<string, IssueFlaggerOutput>
): StrategyRecommendationOutput {
  // If we've reached a leaf node, return its output
  if (node.type === 'strategyRecommendationLeaf') {
    return node.output;
  }

  // For split nodes, determine which child to follow
  if (node.type === 'numericalSplit') {
    const { columnIndex, ranges, defaultChild } = node.split;
    const column = inputColumns[columnIndex];
    const value = getColumnValue(column, morningstarData, surveyData, issueOutputs);
    
    if (value === undefined || typeof value !== 'number') {
      return processStrategyRecommendationTree(defaultChild, inputColumns, morningstarData, surveyData, issueOutputs);
    }

    for (const range of ranges) {
      if (value <= range.maxValue) {
        return processStrategyRecommendationTree(range.childNode, inputColumns, morningstarData, surveyData, issueOutputs);
      }
    }

    return processStrategyRecommendationTree(defaultChild, inputColumns, morningstarData, surveyData, issueOutputs);
  }

  if (node.type === 'categoricalSplit') {
    const { columnIndex, categoryMap, defaultChild } = node.split;
    const column = inputColumns[columnIndex];
    const value = getColumnValue(column, morningstarData, surveyData, issueOutputs);
    
    if (value === undefined) {
      return processStrategyRecommendationTree(defaultChild, inputColumns, morningstarData, surveyData, issueOutputs);
    }

    for (const category of categoryMap) {
      if (value === category.category) {
        return processStrategyRecommendationTree(category.childNode, inputColumns, morningstarData, surveyData, issueOutputs);
      }
    }

    return processStrategyRecommendationTree(defaultChild, inputColumns, morningstarData, surveyData, issueOutputs);
  }

  if (node.type === 'booleanSplit') {
    const { columnIndex, trueChild, falseChild } = node.split;
    const column = inputColumns[columnIndex];
    const value = getColumnValue(column, morningstarData, surveyData, issueOutputs);
    
    // If value is true and we have a true child, use it
    if (value === true && trueChild) {
      return processStrategyRecommendationTree(trueChild, inputColumns, morningstarData, surveyData, issueOutputs);
    }
    // In all other cases (false, undefined, or no trueChild), use the false child
    return processStrategyRecommendationTree(falseChild, inputColumns, morningstarData, surveyData, issueOutputs);
  }

  throw new Error(`Unknown node type: ${(node as any).type}`);
}

/**
 * Main inference function that processes all tables and returns the results
 * This is a convenience wrapper around the more specific functions
 */
export function inferResults(
  morningstarData: MorningstarData,
  surveyData: SurveyData,
  issueFlaggerTables: IssueFlaggerTable[],
  issueTriageTable: IssueTriageTable,
  strategyRecommendationTable: StrategyRecommendationTable
): {
  issueOutputs: Record<string, IssueFlaggerOutput>;
  triageOrder: string[];
  strategyOutput: StrategyRecommendationOutput;
} {
  // Process all issue flagger tables
  const issueOutputs = inferAllIssues(issueFlaggerTables, morningstarData, surveyData);
  
  // Get the triage order
  const triageOrder = getTriageOrder(issueOutputs, issueTriageTable, issueFlaggerTables);
  
  // Process the strategy recommendation table
  const strategyOutput = inferStrategy(
    strategyRecommendationTable,
    morningstarData,
    surveyData,
    issueOutputs
  );
  
  // Return the complete inference result
  return {
    issueOutputs,
    triageOrder,
    strategyOutput: {
      strategyMenu: strategyOutput.strategyMenu || [],
      explanation: strategyOutput.explanation || '',
      extraTags: strategyOutput.extraTags || []
    }
  };
}

// ===== Tree Manipulation Helpers =====

/**
 * Collapse a strategy recommendation tree when columns are deleted
 * This function removes nodes based on deleted columns and adjusts column indices
 */
export function collapseTreeForDeletedColumns(tree: StrategyRecommendationTreeNode, deletedColumnIndex: number): StrategyRecommendationTreeNode {
  if (tree.type === 'strategyRecommendationLeaf') {
    return tree;
  }

  // If this node is based on a deleted column, collapse it by returning its default child
  if (tree.split.columnIndex === deletedColumnIndex) {
    if (tree.type === 'booleanSplit') {
      // For boolean splits, return the falseChild as the default
      return tree.split.falseChild;
    } else {
      return tree.split.defaultChild;
    }
  }

  // If this node is based on a column that comes after the deleted column,
  // we need to adjust its columnIndex
  if (tree.split.columnIndex > deletedColumnIndex) {
    const adjustedSplit = { ...tree.split, columnIndex: tree.split.columnIndex - 1 };
    
    // Recursively process children
    if (tree.type === 'numericalSplit') {
      return {
        ...tree,
        split: {
          ...adjustedSplit,
          defaultChild: collapseTreeForDeletedColumns(tree.split.defaultChild, deletedColumnIndex),
          ranges: tree.split.ranges.map(range => ({
            ...range,
            childNode: collapseTreeForDeletedColumns(range.childNode, deletedColumnIndex)
          }))
        }
      };
    } else if (tree.type === 'categoricalSplit') {
      return {
        ...tree,
        split: {
          ...adjustedSplit,
          defaultChild: collapseTreeForDeletedColumns(tree.split.defaultChild, deletedColumnIndex),
          categoryMap: tree.split.categoryMap.map(category => ({
            ...category,
            childNode: collapseTreeForDeletedColumns(category.childNode, deletedColumnIndex)
          }))
        }
      };
    } else if (tree.type === 'booleanSplit') {
      return {
        ...tree,
        split: {
          ...adjustedSplit,
          trueChild: tree.split.trueChild ? collapseTreeForDeletedColumns(tree.split.trueChild, deletedColumnIndex) : undefined,
          falseChild: collapseTreeForDeletedColumns(tree.split.falseChild, deletedColumnIndex)
        }
      };
    }
  }

  // For nodes based on columns before the deleted column, just process children
  if (tree.type === 'numericalSplit') {
    return {
      ...tree,
      split: {
        ...tree.split,
        defaultChild: collapseTreeForDeletedColumns(tree.split.defaultChild, deletedColumnIndex),
        ranges: tree.split.ranges.map(range => ({
          ...range,
          childNode: collapseTreeForDeletedColumns(range.childNode, deletedColumnIndex)
        }))
      }
    };
  } else if (tree.type === 'categoricalSplit') {
    return {
      ...tree,
      split: {
        ...tree.split,
        defaultChild: collapseTreeForDeletedColumns(tree.split.defaultChild, deletedColumnIndex),
        categoryMap: tree.split.categoryMap.map(category => ({
          ...category,
          childNode: collapseTreeForDeletedColumns(category.childNode, deletedColumnIndex)
        }))
      }
    };
  } else if (tree.type === 'booleanSplit') {
    return {
      ...tree,
      split: {
        ...tree.split,
        trueChild: tree.split.trueChild ? collapseTreeForDeletedColumns(tree.split.trueChild, deletedColumnIndex) : undefined,
        falseChild: collapseTreeForDeletedColumns(tree.split.falseChild, deletedColumnIndex)
      }
    };
  }

  return tree;
}

/**
 * Find which column was deleted by comparing old and new column arrays
 */
export function findDeletedColumnIndex(oldColumns: InputColumn[], newColumns: InputColumn[]): number {
  for (let i = 0; i < oldColumns.length; i++) {
    if (i >= newColumns.length) {
      return i;
    }
    
    // Compare type first
    if (oldColumns[i].type !== newColumns[i].type) {
      return i;
    }
    
    // Compare displayName for types that have it
    if ('displayName' in oldColumns[i] && 'displayName' in newColumns[i]) {
      if ((oldColumns[i] as any).displayName !== (newColumns[i] as any).displayName) {
        return i;
      }
    }
    
    // For other types, compare the specific identifying properties
    if (oldColumns[i].type === 'lightStatus' && newColumns[i].type === 'lightStatus') {
      if ((oldColumns[i] as any).parameterName !== (newColumns[i] as any).parameterName) {
        return i;
      }
    } else if (oldColumns[i].type === 'tags' && newColumns[i].type === 'tags') {
      if ((oldColumns[i] as any).tagID !== (newColumns[i] as any).tagID) {
        return i;
      }
    }
  }
  return -1;
}
